import { supabase } from '@/integrations/supabase/client';

export const CREATOR_AVATARS_BUCKET = 'arsenal-creator-avatars';

const SUPPORTED_AVATAR_EXTENSIONS = ['webp', 'jpg', 'jpeg', 'png', 'avif', 'gif'] as const;

const CONTENT_TYPE_EXTENSION_MAP: Record<string, (typeof SUPPORTED_AVATAR_EXTENSIONS)[number]> = {
  'image/webp': 'webp',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/avif': 'avif',
  'image/gif': 'gif',
};

export function normalizeAvatarContentType(contentType?: string | null): string {
  const normalized = contentType?.split(';')[0]?.trim().toLowerCase() ?? '';
  return normalized.startsWith('image/') ? normalized : 'image/webp';
}

export function getAvatarExtensionFromContentType(contentType?: string | null): string {
  const normalized = normalizeAvatarContentType(contentType);
  return CONTENT_TYPE_EXTENSION_MAP[normalized] ?? 'webp';
}

export function extractAvatarExtensionFromUrl(url?: string | null): string | null {
  if (!url) return null;

  const pathname = (() => {
    try {
      return new URL(url).pathname;
    } catch {
      return url;
    }
  })();

  const match = pathname.match(/\.([a-z0-9]+)$/i);
  const extension = match?.[1]?.toLowerCase() ?? null;

  return extension && SUPPORTED_AVATAR_EXTENSIONS.includes(extension as (typeof SUPPORTED_AVATAR_EXTENSIONS)[number])
    ? extension
    : null;
}

export function getCreatorAvatarStoragePaths(creatorId: string, preferredExtension?: string | null): string[] {
  const extensions = Array.from(
    new Set([preferredExtension, ...SUPPORTED_AVATAR_EXTENSIONS].filter((value): value is string => Boolean(value)))
  );

  return extensions.map((extension) => `${creatorId}.${extension}`);
}

export function getAvatarVersionToken(url?: string | null): string | null {
  if (!url) return null;

  try {
    return new URL(url).searchParams.get('t');
  } catch {
    return null;
  }
}

export function appendAvatarVersion(url: string, version?: string | number | null): string {
  if (!version) return url;

  try {
    const parsed = new URL(url);
    parsed.searchParams.set('t', String(version));
    return parsed.toString();
  } catch {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}t=${encodeURIComponent(String(version))}`;
  }
}

/** True when the URL points to the persisted Storage bucket (i.e. permanent). */
export function isPersistedAvatarUrl(url?: string | null): boolean {
  if (!url) return false;
  return url.includes(`/${CREATOR_AVATARS_BUCKET}/`);
}

/** True for known temporary/external CDN URLs that may expire. */
export function isTikTokCdnUrl(url?: string | null): boolean {
  if (!url) return false;
  return url.includes('tiktokcdn') || url.includes('x-expires');
}

/** Returns the public URL for a candidate file path inside the avatars bucket. */
export function getPersistedAvatarPublicUrl(filePath: string): string {
  const { data } = supabase.storage.from(CREATOR_AVATARS_BUCKET).getPublicUrl(filePath);
  return data.publicUrl;
}

/**
 * Build the full ordered list of candidate URLs to try for a creator's avatar.
 * Order:
 *   1. canonical avatar_url (already persisted in most cases)
 *   2. bucket file path variants — ONLY when we have a known extension hint from avatar_url.
 *      (When avatar_url is NULL we have no extension hint, so trying 6 bucket URLs blindly
 *      just produces 6 wasted 404s and 6 visible flickers in the <img> tag.)
 *   3. source_avatar_url (last-resort temporary CDN — may expire)
 */
export function getCreatorAvatarCandidates(input: {
  creatorId: string;
  avatarUrl?: string | null;
  sourceAvatarUrl?: string | null;
}): string[] {
  const { creatorId, avatarUrl, sourceAvatarUrl } = input;
  const seen = new Set<string>();
  const out: string[] = [];

  const push = (url?: string | null) => {
    if (!url) return;
    const trimmed = url.trim();
    if (!trimmed || seen.has(trimmed)) return;
    seen.add(trimmed);
    out.push(trimmed);
  };

  const preferredExt = extractAvatarExtensionFromUrl(avatarUrl);
  const versionToken = getAvatarVersionToken(avatarUrl);

  push(avatarUrl);

  // Only try bucket variants when we have a hint that something is actually there.
  // Either the canonical avatar_url already points at a known extension, OR the source URL
  // hints at an extension. Without any hint, the bucket lookups are guaranteed to 404.
  const sourceExt = extractAvatarExtensionFromUrl(sourceAvatarUrl);
  const extensionHint = preferredExt ?? sourceExt;
  if (extensionHint) {
    for (const filePath of getCreatorAvatarStoragePaths(creatorId, extensionHint)) {
      push(appendAvatarVersion(getPersistedAvatarPublicUrl(filePath), versionToken));
    }
  }

  push(sourceAvatarUrl);

  return out;
}
